import React, { useCallback, useEffect, useRef, useState } from "react";
import {
    Sparkles,
    FileText,
    Download,
    Loader2,
    Wand2,
    CheckCircle2,
    AlertCircle,
    Pencil,
    Save,
    X,
    Plus,
    Trash2,
    Upload,
    Inbox,
    ArrowLeft,
    FileUp,
    RefreshCw,
} from "lucide-react";
import { motion } from "framer-motion";
import { Layout } from "@/components/Layout";
import { TourButton } from "@/components/TourButton";
import { useTour } from "@/hooks/useTour";
import { memoriaStandaloneTour } from "@/tours";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
    AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import {
    msList,
    msCreate,
    msGet,
    msExtractRequirements,
    msGenerate,
    msRefine,
    msSaveContent,
    msDelete,
    msDownloadUrl,
} from "@/lib/api";

function getAuthToken() {
    try {
        return (
            localStorage.getItem("auth_token") ||
            localStorage.getItem("token") ||
            ""
        );
    } catch {
        return "";
    }
}

function fmtRel(iso) {
    if (!iso) return "—";
    try {
        const diff = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
        if (diff < 60) return "ahora";
        if (diff < 3600) return `hace ${Math.round(diff / 60)} min`;
        if (diff < 86400) return `hace ${Math.round(diff / 3600)} h`;
        if (diff < 86400 * 7) return `hace ${Math.round(diff / 86400)} d`;
        return new Date(iso).toLocaleDateString("es-ES");
    } catch {
        return "—";
    }
}

function renderMarkdown(md) {
    if (!md) return null;
    const lines = md.split("\n");
    const out = [];
    let listBuffer = [];
    const flushList = () => {
        if (listBuffer.length) {
            out.push(
                <ul
                    key={`l${out.length}`}
                    className="list-disc list-inside text-sm text-slate-700 my-2 space-y-1 ml-2"
                >
                    {listBuffer.map((b, i) => (
                        <li key={i} dangerouslySetInnerHTML={{ __html: b }} />
                    ))}
                </ul>,
            );
            listBuffer = [];
        }
    };
    const inline = (s) =>
        s
            .replace(/&/g, "&amp;")
            .replace(/</g, "&lt;")
            .replace(/>/g, "&gt;")
            .replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>")
            .replace(/(?<!\*)\*([^*]+)\*(?!\*)/g, "<em>$1</em>");
    lines.forEach((raw, idx) => {
        const line = raw.trimEnd();
        if (!line) {
            flushList();
            return;
        }
        if (line.startsWith("# ")) {
            flushList();
            out.push(
                <h1
                    key={idx}
                    className="font-display text-2xl font-black mt-4 mb-3 text-slate-900 text-center"
                    dangerouslySetInnerHTML={{ __html: inline(line.slice(2)) }}
                />,
            );
        } else if (line.startsWith("## ")) {
            flushList();
            out.push(
                <h2
                    key={idx}
                    className="font-display text-lg font-bold mt-5 mb-2 text-slate-900"
                    dangerouslySetInnerHTML={{ __html: inline(line.slice(3)) }}
                />,
            );
        } else if (line.startsWith("### ")) {
            flushList();
            out.push(
                <h3
                    key={idx}
                    className="font-display text-base font-semibold mt-4 mb-1 text-slate-800"
                    dangerouslySetInnerHTML={{ __html: inline(line.slice(4)) }}
                />,
            );
        } else if (/^[-*]\s+/.test(line)) {
            listBuffer.push(inline(line.replace(/^[-*]\s+/, "")));
        } else {
            flushList();
            out.push(
                <p
                    key={idx}
                    className="text-sm text-slate-700 leading-relaxed my-2 text-justify"
                    dangerouslySetInnerHTML={{ __html: inline(line) }}
                />,
            );
        }
    });
    flushList();
    return out;
}

function NewSessionDialog({ onCreated, open, onOpenChange }) {
    const [name, setName] = useState("");
    const [files, setFiles] = useState([]);
    const [busy, setBusy] = useState(false);
    const inputRef = useRef(null);

    const reset = () => {
        setName("");
        setFiles([]);
    };

    const handleFiles = (list) => {
        const next = Array.from(list || []).filter(
            (f) =>
                /\.pdf$/i.test(f.name) ||
                /\.txt$/i.test(f.name) ||
                /\.docx?$/i.test(f.name),
        );
        if (next.length === 0) {
            toast.error("Sube PDFs (también acepta .txt).");
            return;
        }
        setFiles((prev) => [...prev, ...next].slice(0, 8));
    };

    const handleSubmit = async () => {
        if (!name.trim()) {
            toast.error("Pon un nombre a la memoria.");
            return;
        }
        if (files.length === 0) {
            toast.error("Sube al menos un PDF.");
            return;
        }
        setBusy(true);
        try {
            const rec = await msCreate(name.trim(), files);
            toast.success("Sesión creada. Extrayendo restricciones…");
            onCreated(rec);
            reset();
            onOpenChange(false);
        } catch (e) {
            toast.error(
                e?.response?.data?.detail || "No se pudo crear la sesión.",
            );
        } finally {
            setBusy(false);
        }
    };

    return (
        <AlertDialog open={open} onOpenChange={onOpenChange}>
            <AlertDialogContent
                className="max-w-xl"
                data-testid="ms-new-dialog"
            >
                <AlertDialogHeader>
                    <AlertDialogTitle>Nueva memoria técnica</AlertDialogTitle>
                    <AlertDialogDescription>
                        Sube los pliegos (PCAP, PPT) y dale un nombre. La IA
                        leerá las restricciones formales y podrás generar la
                        memoria con uno o varios prompts.
                    </AlertDialogDescription>
                </AlertDialogHeader>
                <div className="space-y-4">
                    <div>
                        <label className="text-xs font-bold uppercase tracking-wider text-slate-500">
                            Nombre de la memoria
                        </label>
                        <Input
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            placeholder="Ej: Limpieza Hospital La Paz · marzo 2026"
                            className="mt-1 rounded-xl"
                            data-testid="ms-new-name"
                        />
                    </div>
                    <div>
                        <label className="text-xs font-bold uppercase tracking-wider text-slate-500">
                            Pliegos PDF · máx 8
                        </label>
                        <div
                            onDragOver={(e) => e.preventDefault()}
                            onDrop={(e) => {
                                e.preventDefault();
                                handleFiles(e.dataTransfer.files);
                            }}
                            onClick={() => inputRef.current?.click()}
                            className="mt-1 rounded-xl border-2 border-dashed border-violet-300 bg-violet-50/60 hover:bg-violet-100/60 transition-colors p-6 text-center cursor-pointer"
                            data-testid="ms-new-dropzone"
                        >
                            <FileUp
                                className="mx-auto text-violet-500 mb-2"
                                size={28}
                            />
                            <div className="text-sm font-medium text-slate-800">
                                Arrastra los PDFs aquí o haz clic
                            </div>
                            <div className="text-xs text-slate-500 mt-1">
                                .pdf · máx 25 MB cada uno
                            </div>
                            <input
                                ref={inputRef}
                                type="file"
                                multiple
                                accept=".pdf,.txt,.docx"
                                className="hidden"
                                onChange={(e) => handleFiles(e.target.files)}
                            />
                        </div>
                        {files.length > 0 && (
                            <ul className="mt-3 space-y-1.5">
                                {files.map((f, i) => (
                                    <li
                                        key={i}
                                        className="text-xs flex items-center justify-between bg-slate-50 rounded-lg px-3 py-2 border border-slate-200"
                                    >
                                        <span className="truncate flex-1 mr-2">
                                            <FileText
                                                size={12}
                                                className="inline mr-1.5 text-slate-400"
                                            />
                                            {f.name}
                                        </span>
                                        <span className="text-slate-500 font-mono mr-2">
                                            {(f.size / 1024).toFixed(0)} KB
                                        </span>
                                        <button
                                            type="button"
                                            onClick={() =>
                                                setFiles((prev) =>
                                                    prev.filter(
                                                        (_, j) => j !== i,
                                                    ),
                                                )
                                            }
                                            className="text-slate-400 hover:text-red-500"
                                        >
                                            <X size={12} />
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                </div>
                <AlertDialogFooter>
                    <AlertDialogCancel
                        disabled={busy}
                        onClick={reset}
                        data-testid="ms-new-cancel"
                    >
                        Cancelar
                    </AlertDialogCancel>
                    <Button
                        onClick={handleSubmit}
                        disabled={busy}
                        className="rounded-xl bg-violet-600 hover:bg-violet-700 text-white"
                        data-testid="ms-new-submit"
                    >
                        {busy ? (
                            <Loader2
                                size={14}
                                className="mr-2 animate-spin"
                            />
                        ) : (
                            <Upload size={14} className="mr-2" />
                        )}
                        {busy ? "Subiendo…" : "Crear sesión"}
                    </Button>
                </AlertDialogFooter>
            </AlertDialogContent>
        </AlertDialog>
    );
}

function ReqRow({ label, value }) {
    if (value === null || value === undefined || value === "") return null;
    if (Array.isArray(value) && value.length === 0) return null;
    let display;
    if (Array.isArray(value)) {
        display = (
            <ul className="list-disc list-inside text-sm text-slate-700 mt-1 space-y-0.5">
                {value.slice(0, 12).map((v, i) => (
                    <li key={i}>
                        {typeof v === "object" ? JSON.stringify(v) : String(v)}
                    </li>
                ))}
            </ul>
        );
    } else {
        display = (
            <span className="font-mono text-slate-900 font-semibold">
                {String(value)}
            </span>
        );
    }
    return (
        <div className="py-2 border-b border-slate-100 last:border-0">
            <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
                {label}
            </div>
            <div className="mt-0.5 text-sm">{display}</div>
        </div>
    );
}

function SessionEditor({ sid, onBack, onChange }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [generating, setGenerating] = useState(false);
    const [refining, setRefining] = useState(false);
    const [extracting, setExtracting] = useState(false);
    const [extraInstructions, setExtraInstructions] = useState("");
    const [refineText, setRefineText] = useState("");
    const [editMode, setEditMode] = useState(false);
    const [editBuffer, setEditBuffer] = useState("");
    const [saving, setSaving] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const d = await msGet(sid);
            setData(d || {});
        } catch (e) {
            console.error(e);
            toast.error("No se pudo cargar la sesión.");
        } finally {
            setLoading(false);
        }
    }, [sid]);

    useEffect(() => {
        load();
    }, [load]);

    // Auto-trigger extraction on first load if requirements missing.
    const autoExtractedRef = useRef(false);
    useEffect(() => {
        if (
            data &&
            !autoExtractedRef.current &&
            !data.requirements &&
            !data.generating &&
            !data.error
        ) {
            autoExtractedRef.current = true;
            (async () => {
                try {
                    setData((p) => ({ ...(p || {}), generating: true }));
                    await msExtractRequirements(sid);
                } catch (e) {
                    const isTimeout =
                        e?.code === "ECONNABORTED" ||
                        /timeout/i.test(e?.message || "");
                    if (!isTimeout) {
                        setData((p) => ({ ...(p || {}), generating: false }));
                    }
                }
            })();
        }
    }, [data, sid]);

    // Poll while a background job is running.
    useEffect(() => {
        if (!data?.generating) return;
        const t = setInterval(async () => {
            try {
                const d = await msGet(sid);
                setData(d || {});
                if (!d?.generating) {
                    clearInterval(t);
                    if (d?.error) toast.error(`Falló: ${d.error}`);
                    else if (d?.content)
                        toast.success("Memoria lista.");
                    else if (d?.requirements)
                        toast.success("Restricciones extraídas.");
                    onChange?.();
                }
            } catch (e) {
                console.error(e);
            }
        }, 4000);
        return () => clearInterval(t);
    }, [data?.generating, sid, onChange]);

    const handleExtract = async () => {
        setExtracting(true);
        setData((p) => ({ ...(p || {}), generating: true }));
        toast.success("Analizando pliegos… 30-60 s.");
        try {
            await msExtractRequirements(sid);
        } catch (e) {
            const isTimeout =
                e?.code === "ECONNABORTED" ||
                /timeout/i.test(e?.message || "");
            if (!isTimeout) {
                toast.error(
                    e?.response?.data?.detail || "No se pudo extraer.",
                );
                setData((p) => ({ ...(p || {}), generating: false }));
            }
        } finally {
            setExtracting(false);
        }
    };

    const handleGenerate = async () => {
        setGenerating(true);
        setData((p) => ({ ...(p || {}), generating: true }));
        toast.success("Redactando la memoria… 30-60 s.");
        try {
            await msGenerate(sid, extraInstructions);
            setExtraInstructions("");
        } catch (e) {
            const isTimeout =
                e?.code === "ECONNABORTED" ||
                /timeout/i.test(e?.message || "");
            if (!isTimeout) {
                toast.error(
                    e?.response?.data?.detail || "No se pudo generar.",
                );
                setData((p) => ({ ...(p || {}), generating: false }));
            } else {
                setExtraInstructions("");
            }
        } finally {
            setGenerating(false);
        }
    };

    const handleRefine = async () => {
        const txt = refineText.trim();
        if (!txt) {
            toast.error("Escribe una instrucción.");
            return;
        }
        setRefining(true);
        setData((p) => ({ ...(p || {}), generating: true }));
        toast.success("Aplicando cambios… 30-60 s.");
        try {
            await msRefine(sid, txt);
            setRefineText("");
        } catch (e) {
            const isTimeout =
                e?.code === "ECONNABORTED" ||
                /timeout/i.test(e?.message || "");
            if (!isTimeout) {
                toast.error(
                    e?.response?.data?.detail || "No se pudo refinar.",
                );
                setData((p) => ({ ...(p || {}), generating: false }));
            } else {
                setRefineText("");
            }
        } finally {
            setRefining(false);
        }
    };

    const handleDownload = async (format) => {
        try {
            const res = await fetch(msDownloadUrl(sid, format), {
                headers: { Authorization: `Bearer ${getAuthToken()}` },
                credentials: "include",
            });
            if (!res.ok) {
                const j = await res.json().catch(() => ({}));
                throw new Error(j.detail || `HTTP ${res.status}`);
            }
            const blob = await res.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `memoria_tecnica.${format}`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
        } catch (e) {
            toast.error(e?.message || "No se pudo descargar.");
        }
    };

    const handleStartEdit = () => {
        setEditBuffer(data?.content || "");
        setEditMode(true);
    };
    const handleCancelEdit = () => {
        setEditMode(false);
        setEditBuffer("");
    };
    const handleSaveEdit = async () => {
        if (!editBuffer.trim()) {
            toast.error("El contenido no puede estar vacío.");
            return;
        }
        setSaving(true);
        try {
            await msSaveContent(sid, editBuffer);
            setEditMode(false);
            await load();
            toast.success("Memoria guardada.");
            onChange?.();
        } catch (e) {
            toast.error(
                e?.response?.data?.detail || "No se pudo guardar.",
            );
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div className="space-y-3">
                <Skeleton className="h-24 w-full" />
                <Skeleton className="h-64 w-full" />
            </div>
        );
    }

    const requirements = data?.requirements || null;
    const content = data?.content || "";
    const stats = data?.stats || null;

    return (
        <div className="space-y-6" data-testid="ms-editor">
            <div className="flex items-center justify-between gap-3 flex-wrap">
                <button
                    onClick={onBack}
                    className="text-xs font-medium text-slate-600 hover:text-slate-900 inline-flex items-center gap-1.5"
                    data-testid="ms-back-btn"
                >
                    <ArrowLeft size={13} />
                    Volver a la lista
                </button>
                <div className="text-[10px] text-slate-400 font-mono">
                    Creada {fmtRel(data?.created_at)} · {(data?.pliego_files || []).length} PDF
                </div>
            </div>

            <div>
                <div className="text-[10px] font-bold uppercase tracking-wider text-violet-600">
                    Memoria técnica · sesión
                </div>
                <h1 className="font-display text-3xl font-black text-slate-900 mt-1">
                    {data?.name || "Sin nombre"}
                </h1>
            </div>

            {/* Step 1: Requirements */}
            <div className="rounded-2xl border-2 border-violet-200 bg-violet-50/40 p-5">
                <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div className="flex-1 min-w-0">
                        <div className="text-[10px] font-bold uppercase tracking-wider text-violet-600">
                            Paso 1
                        </div>
                        <h3 className="font-display text-xl font-semibold mt-1 text-slate-900">
                            Restricciones formales del pliego
                        </h3>
                        <p className="text-sm text-slate-600 mt-1">
                            Tipografía, tamaño, número de páginas/palabras,
                            secciones obligatorias…
                        </p>
                    </div>
                    <Button
                        onClick={handleExtract}
                        disabled={extracting || !!data?.generating}
                        className="rounded-xl bg-violet-600 hover:bg-violet-700 text-white shrink-0"
                        data-testid="ms-extract-btn"
                    >
                        {extracting ||
                        (data?.generating && !data?.content && !requirements) ? (
                            <Loader2 size={14} className="mr-2 animate-spin" />
                        ) : (
                            <Sparkles size={14} className="mr-2" />
                        )}
                        {requirements ? "Re-extraer" : "Extraer restricciones"}
                    </Button>
                </div>
                {data?.error && !data?.generating && (
                    <div className="mt-3 text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2 inline-flex items-center gap-2">
                        <AlertCircle size={13} />
                        {data.error}
                    </div>
                )}
                {requirements && (
                    <div className="mt-4 bg-white rounded-xl p-4 border border-violet-100">
                        <ReqRow label="Máx. páginas" value={requirements.max_pages} />
                        <ReqRow label="Máx. palabras" value={requirements.max_words} />
                        <ReqRow
                            label="Máx. caracteres"
                            value={requirements.max_characters}
                        />
                        <ReqRow label="Tipografía" value={requirements.font_family} />
                        <ReqRow
                            label="Tamaño de letra"
                            value={requirements.font_size_pt}
                        />
                        <ReqRow
                            label="Interlineado"
                            value={requirements.line_spacing}
                        />
                        <ReqRow
                            label="Márgenes (cm)"
                            value={requirements.margin_cm}
                        />
                        <ReqRow
                            label="Tamaño de página"
                            value={requirements.page_size}
                        />
                        <ReqRow
                            label="Formato de entrega"
                            value={requirements.submission_format}
                        />
                        <ReqRow
                            label="Secciones obligatorias"
                            value={requirements.sections_required}
                        />
                        <ReqRow
                            label="Criterios puntuables"
                            value={(requirements.criteria_weights || []).map(
                                (c) => `${c.name}: ${c.weight_pct}%`,
                            )}
                        />
                        <ReqRow
                            label="Notas adicionales"
                            value={requirements.notes}
                        />
                    </div>
                )}
            </div>

            {/* Step 2: Generate */}
            <div className="rounded-2xl border-2 border-emerald-200 bg-emerald-50/40 p-5">
                <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div className="flex-1 min-w-0">
                        <div className="text-[10px] font-bold uppercase tracking-wider text-emerald-600">
                            Paso 2
                        </div>
                        <h3 className="font-display text-xl font-semibold mt-1 text-slate-900">
                            Generar la memoria técnica
                        </h3>
                        <p className="text-sm text-slate-600 mt-1">
                            Claude redacta la memoria respetando las
                            restricciones anteriores y la solvencia de la
                            empresa (de tu perfil).
                        </p>
                    </div>
                    <Button
                        onClick={handleGenerate}
                        disabled={generating || !!data?.generating}
                        className="rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white shrink-0"
                        data-testid="ms-generate-btn"
                    >
                        {generating ||
                        (data?.generating && requirements && !content) ? (
                            <Loader2 size={14} className="mr-2 animate-spin" />
                        ) : (
                            <FileText size={14} className="mr-2" />
                        )}
                        {content ? "Regenerar desde cero" : "Generar memoria"}
                    </Button>
                </div>
                {!content && (
                    <Textarea
                        value={extraInstructions}
                        onChange={(e) => setExtraInstructions(e.target.value)}
                        placeholder="(Opcional) Instrucciones específicas para la primera redacción…"
                        className="mt-3 rounded-xl bg-white border-emerald-200"
                        rows={2}
                        data-testid="ms-extra-instructions"
                    />
                )}
            </div>

            {/* Step 3: Preview + Edit + Refine + Download */}
            {content && (
                <>
                    <div className="rounded-2xl bg-white border-2 border-slate-200 overflow-hidden">
                        <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between gap-3 flex-wrap bg-slate-50/50">
                            <div className="flex items-center gap-2">
                                <CheckCircle2
                                    size={16}
                                    className="text-emerald-500"
                                />
                                <span className="font-display text-base font-semibold">
                                    Vista previa
                                </span>
                                {stats && (
                                    <span className="text-xs text-slate-500 font-mono ml-2">
                                        ≈ {stats.words?.toLocaleString("es-ES")}{" "}
                                        palabras ·{" "}
                                        {stats.characters?.toLocaleString(
                                            "es-ES",
                                        )}{" "}
                                        car. · {stats.approx_pages} pág.
                                    </span>
                                )}
                            </div>
                            <div className="flex gap-2">
                                {editMode ? (
                                    <>
                                        <Button
                                            size="sm"
                                            variant="outline"
                                            className="rounded-xl"
                                            onClick={handleCancelEdit}
                                            disabled={saving}
                                            data-testid="ms-cancel-edit-btn"
                                        >
                                            <X size={12} className="mr-1.5" />
                                            Cancelar
                                        </Button>
                                        <Button
                                            size="sm"
                                            className="rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white"
                                            onClick={handleSaveEdit}
                                            disabled={saving}
                                            data-testid="ms-save-edit-btn"
                                        >
                                            {saving ? (
                                                <Loader2
                                                    size={12}
                                                    className="mr-1.5 animate-spin"
                                                />
                                            ) : (
                                                <Save
                                                    size={12}
                                                    className="mr-1.5"
                                                />
                                            )}
                                            Guardar cambios
                                        </Button>
                                    </>
                                ) : (
                                    <>
                                        <Button
                                            size="sm"
                                            variant="outline"
                                            className="rounded-xl"
                                            onClick={handleStartEdit}
                                            disabled={!!data?.generating}
                                            data-testid="ms-edit-btn"
                                        >
                                            <Pencil size={12} className="mr-1.5" />
                                            Editar
                                        </Button>
                                        <Button
                                            size="sm"
                                            variant="outline"
                                            className="rounded-xl"
                                            onClick={() => handleDownload("docx")}
                                            data-testid="ms-download-docx-btn"
                                        >
                                            <Download size={12} className="mr-1.5" />
                                            Word
                                        </Button>
                                        <Button
                                            size="sm"
                                            className="rounded-xl bg-[#0055ff] hover:bg-[#0044cc] text-white"
                                            onClick={() => handleDownload("pdf")}
                                            data-testid="ms-download-pdf-btn"
                                        >
                                            <Download size={12} className="mr-1.5" />
                                            PDF
                                        </Button>
                                    </>
                                )}
                            </div>
                        </div>
                        {editMode ? (
                            <div className="p-5">
                                <div className="text-[11px] text-slate-500 mb-2 font-mono">
                                    Markdown · usa <code>#</code> H1,{" "}
                                    <code>##</code> H2, <code>###</code> H3,{" "}
                                    <code>-</code> listas,{" "}
                                    <code>**negrita**</code>.
                                </div>
                                <Textarea
                                    value={editBuffer}
                                    onChange={(e) =>
                                        setEditBuffer(e.target.value)
                                    }
                                    className="font-mono text-sm bg-white border-slate-200 rounded-xl min-h-[600px]"
                                    rows={30}
                                    data-testid="ms-edit-textarea"
                                />
                                <div className="mt-2 text-[11px] text-slate-400 font-mono">
                                    {editBuffer.length.toLocaleString("es-ES")}{" "}
                                    caracteres ·{" "}
                                    {(
                                        editBuffer.match(/\S+/g) || []
                                    ).length.toLocaleString("es-ES")}{" "}
                                    palabras
                                </div>
                            </div>
                        ) : (
                            <div
                                className="p-8 max-h-[700px] overflow-y-auto"
                                data-testid="ms-content-preview"
                            >
                                {renderMarkdown(content)}
                            </div>
                        )}
                    </div>

                    <div className="rounded-2xl border-2 border-amber-200 bg-amber-50/40 p-5">
                        <div className="text-[10px] font-bold uppercase tracking-wider text-amber-600">
                            Paso 3 (opcional)
                        </div>
                        <h3 className="font-display text-xl font-semibold mt-1 text-slate-900">
                            Refinar con IA
                        </h3>
                        <p className="text-sm text-slate-600 mt-1">
                            Pídele a la IA cambios concretos. "hazla más corta",
                            "elimina la sección de riesgos"…
                        </p>
                        <div className="flex gap-2 mt-3">
                            <Textarea
                                value={refineText}
                                onChange={(e) => setRefineText(e.target.value)}
                                placeholder="Escribe tu instrucción…"
                                className="rounded-xl bg-white border-amber-200 flex-1"
                                rows={2}
                                data-testid="ms-refine-input"
                            />
                            <Button
                                onClick={handleRefine}
                                disabled={
                                    refining ||
                                    !refineText.trim() ||
                                    !!data?.generating
                                }
                                className="rounded-xl bg-amber-600 hover:bg-amber-700 text-white self-stretch px-5"
                                data-testid="ms-refine-btn"
                            >
                                {refining || data?.generating ? (
                                    <Loader2
                                        size={14}
                                        className="animate-spin"
                                    />
                                ) : (
                                    <Wand2 size={14} />
                                )}
                            </Button>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
}

export default function MemoriaStandalone() {
    const tour = useTour("tour:memoria-standalone", memoriaStandaloneTour, { autoStart: true });
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [openSid, setOpenSid] = useState(null);
    const [dialogOpen, setDialogOpen] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const d = await msList();
            setItems(d?.items || []);
        } catch (e) {
            console.error(e);
            setItems([]);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const handleDelete = async (sid) => {
        try {
            await msDelete(sid);
            toast.success("Memoria eliminada.");
            if (openSid === sid) setOpenSid(null);
            load();
        } catch (e) {
            toast.error(
                e?.response?.data?.detail || "No se pudo eliminar.",
            );
        }
    };

    return (
        <Layout>
            <div
                className="max-w-6xl mx-auto px-4 sm:px-6 py-8 space-y-6"
                data-testid="memoria-standalone-page"
            >
                {openSid ? (
                    <SessionEditor
                        key={openSid}
                        sid={openSid}
                        onBack={() => {
                            setOpenSid(null);
                            load();
                        }}
                        onChange={load}
                    />
                ) : (
                    <>
                        <motion.div
                            initial={{ opacity: 0, y: 12 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.4 }}
                            className="flex items-end justify-between gap-4 flex-wrap"
                        >
                            <div>
                                <div className="text-[10px] font-bold uppercase tracking-wider text-violet-600">
                                    Herramienta independiente
                                </div>
                                <h1 className="font-display text-4xl sm:text-5xl font-black text-slate-900 mt-1">
                                    Memoria técnica
                                </h1>
                                <p className="text-sm text-slate-600 mt-2 max-w-2xl">
                                    Sube los pliegos de cualquier licitación y
                                    la IA genera la memoria técnica respetando
                                    las restricciones formales. Puedes editar el
                                    texto inline y refinarlo cuantas veces
                                    quieras antes de descargar Word o PDF.
                                </p>
                            </div>
                            <div className="flex gap-2">
                                <Button
                                    variant="outline"
                                    onClick={load}
                                    className="rounded-xl"
                                    data-testid="ms-refresh-btn"
                                >
                                    <RefreshCw size={14} className="mr-2" />
                                    Refrescar
                                </Button>
                                <Button
                                    onClick={() => setDialogOpen(true)}
                                    className="rounded-xl bg-violet-600 hover:bg-violet-700 text-white shadow-md"
                                    data-testid="ms-new-btn"
                                >
                                    <Plus size={14} className="mr-2" />
                                    Nueva memoria
                                </Button>
                            </div>
                        </motion.div>

                        {loading ? (
                            <div className="space-y-3">
                                <Skeleton className="h-20 w-full" />
                                <Skeleton className="h-20 w-full" />
                                <Skeleton className="h-20 w-full" />
                            </div>
                        ) : items.length === 0 ? (
                            <div className="rounded-2xl border-2 border-dashed border-slate-200 bg-white p-12 text-center">
                                <Inbox
                                    className="mx-auto text-slate-300 mb-3"
                                    size={42}
                                />
                                <div className="font-display text-lg font-semibold text-slate-700">
                                    Aún no has creado ninguna memoria
                                </div>
                                <div className="text-sm text-slate-500 mt-1">
                                    Sube los pliegos de una licitación para
                                    empezar.
                                </div>
                                <Button
                                    onClick={() => setDialogOpen(true)}
                                    className="mt-5 rounded-xl bg-violet-600 hover:bg-violet-700 text-white"
                                    data-testid="ms-empty-cta"
                                >
                                    <Plus size={14} className="mr-2" />
                                    Crear la primera memoria
                                </Button>
                            </div>
                        ) : (
                            <div className="space-y-3">
                                {items.map((it, idx) => (
                                    <motion.div
                                        key={it.id}
                                        initial={{ opacity: 0, y: 8 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{
                                            duration: 0.25,
                                            delay: idx * 0.04,
                                        }}
                                        className="rounded-2xl bg-white border-2 border-slate-200 hover:border-violet-300 hover:shadow-md transition-all p-5 flex items-center gap-4 group"
                                        data-testid={`ms-item-${it.id}`}
                                    >
                                        <div
                                            className={`size-12 rounded-xl flex items-center justify-center shrink-0 ${
                                                it.generating
                                                    ? "bg-amber-100 text-amber-600"
                                                    : it.stats?.words
                                                    ? "bg-emerald-100 text-emerald-600"
                                                    : it.requirements
                                                    ? "bg-violet-100 text-violet-600"
                                                    : "bg-slate-100 text-slate-500"
                                            }`}
                                        >
                                            {it.generating ? (
                                                <Loader2
                                                    size={20}
                                                    className="animate-spin"
                                                />
                                            ) : it.stats?.words ? (
                                                <CheckCircle2 size={20} />
                                            ) : (
                                                <FileText size={20} />
                                            )}
                                        </div>
                                        <button
                                            onClick={() => setOpenSid(it.id)}
                                            className="flex-1 min-w-0 text-left"
                                            data-testid={`ms-open-${it.id}`}
                                        >
                                            <div className="font-display text-base font-semibold text-slate-900 truncate group-hover:text-violet-700 transition-colors">
                                                {it.name || "Sin nombre"}
                                            </div>
                                            <div className="text-xs text-slate-500 mt-1 flex items-center gap-3 flex-wrap">
                                                <span>
                                                    {(it.pliego_files || []).length}{" "}
                                                    PDF
                                                </span>
                                                <span>·</span>
                                                <span>
                                                    Creada {fmtRel(it.created_at)}
                                                </span>
                                                {it.stats?.words ? (
                                                    <>
                                                        <span>·</span>
                                                        <span className="font-mono">
                                                            ≈{" "}
                                                            {it.stats.words.toLocaleString(
                                                                "es-ES",
                                                            )}{" "}
                                                            palabras
                                                        </span>
                                                    </>
                                                ) : null}
                                                {it.generating ? (
                                                    <>
                                                        <span>·</span>
                                                        <span className="text-amber-600 font-semibold">
                                                            Generando…
                                                        </span>
                                                    </>
                                                ) : null}
                                            </div>
                                        </button>
                                        <Button
                                            size="sm"
                                            variant="outline"
                                            onClick={() => setOpenSid(it.id)}
                                            className="rounded-xl"
                                            data-testid={`ms-open-btn-${it.id}`}
                                        >
                                            Abrir
                                        </Button>
                                        <AlertDialog>
                                            <AlertDialogTrigger asChild>
                                                <Button
                                                    size="sm"
                                                    variant="ghost"
                                                    className="rounded-xl text-slate-400 hover:text-red-600"
                                                    data-testid={`ms-delete-btn-${it.id}`}
                                                >
                                                    <Trash2 size={14} />
                                                </Button>
                                            </AlertDialogTrigger>
                                            <AlertDialogContent>
                                                <AlertDialogHeader>
                                                    <AlertDialogTitle>
                                                        ¿Eliminar esta memoria?
                                                    </AlertDialogTitle>
                                                    <AlertDialogDescription>
                                                        Se borrará la sesión, los
                                                        PDFs y el contenido
                                                        generado.
                                                    </AlertDialogDescription>
                                                </AlertDialogHeader>
                                                <AlertDialogFooter>
                                                    <AlertDialogCancel>
                                                        Cancelar
                                                    </AlertDialogCancel>
                                                    <AlertDialogAction
                                                        className="bg-red-600 hover:bg-red-700"
                                                        onClick={() =>
                                                            handleDelete(it.id)
                                                        }
                                                    >
                                                        Eliminar
                                                    </AlertDialogAction>
                                                </AlertDialogFooter>
                                            </AlertDialogContent>
                                        </AlertDialog>
                                    </motion.div>
                                ))}
                            </div>
                        )}
                    </>
                )}

                <NewSessionDialog
                    open={dialogOpen}
                    onOpenChange={setDialogOpen}
                    onCreated={(rec) => {
                        setItems((prev) => [rec, ...prev]);
                        setOpenSid(rec.id);
                    }}
                />
            </div>
            <TourButton onClick={tour.start} label="Ver tour" />
        </Layout>
    );
}
